<template>
	<div
		v-if="toaster.toasts.length"
		aria-live="assertive"
		class="pointer-events-none fixed inset-0 flex items-end px-4 py-6 sm:items-start sm:p- z-[60]">
		<div class="flex w-full flex-col items-center space-y-4 sm:items-end">
			<transition
				v-for="(toast, index) in toaster.toasts"
				:key="index"
				enter-active-class="transform ease-out duration-300 transition"
				enter-from-class="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2"
				enter-to-class="translate-y-0 opacity-100 sm:translate-x-0"
				leave-active-class="transition ease-in duration-100"
				leave-from-class="opacity-100"
				leave-to-class="opacity-0"
				data-testid="toast">
				<div
					v-show="toast"
					class="pointer-events-auto w-full max-w-sm overflow-hidden rounded-lg shadow-lg ring-1 ring-black ring-opacity-5"
					:class="{
						'bg-white': toast.status != 'error',
						'bg-red-600': toast.status == 'error'
					}"
					data-testid="body">
					<div class="p-4">
						<div class="flex items-start">
							<div class="flex-shrink-0">
								<CheckCircleIcon
									v-if="toast.status == 'success'"
									class="h-6 w-6 text-green-400"
									aria-hidden="true"
									data-testid="success-icon" />
								<XCircleIcon
									v-if="toast.status == 'error'"
									class="h-6 w-6 text-white"
									aria-hidden="true"
									data-testid="error-icon" />
								<ExclamationTriangleIcon
									v-if="toast.status == 'warning'"
									class="h-6 w-6 text-yellow-400"
									aria-hidden="true"
									data-testid="warning-icon" />
								<Cog6ToothIcon
									v-if="toast.status == 'working'"
									class="h-6 w-6 text-blue-400 animate-spin working-icon"
									aria-hidden="true"
									data-testid="working-icon" />
							</div>
							<div class="ml-3 w-0 flex-1 pt-0.5">
								<p
									data-testid="title"
									class="text-sm font-medium"
									:class="{
										'text-gray-900': toast.status != 'error',
										'text-white': toast.status == 'error'
									}">
									<span v-if="toast.status == 'error'">Uh oh!</span>
									<span v-else-if="toast.status == 'working'">Working...</span>
									<span v-else>Notice</span>
								</p>
								<p
									data-testid="text"
									class="mt-1 text-sm"
									:class="{
										'text-gray-500': toast.status != 'error',
										'text-white': toast.status == 'error'
									}">
									{{ toast.text }}
								</p>

								<div v-if="toast.action" class="mt-3 flex space-x-7">
									<button
										type="button"
										:class="buttonClasses"
										class="text-mabey-800 hover:text-mabey-700 focus:ring-mabey-700"
										data-testid="action"
										@click="action(toast.action.method, toast.id)">
										{{ toast.action.text }}
									</button>
									<button
										v-if="toast.dismissable"
										type="button"
										:class="buttonClasses"
										class="text-gray-700 hover:text-gray-500 focus:ring-indigo-500"
										data-testid="dismiss"
										@click="toaster.dismiss(toast.id)">
										Dismiss
									</button>
								</div>
							</div>
							<div class="ml-4 flex flex-shrink-0">
								<button
									v-if="toast.dismissable"
									type="button"
									class="inline-flex rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
									:class="{
										'text-gray-400 hover:text-gray-500':
											toast.status != 'error',
										'text-gray-100 hover:text-white': toast.status == 'error'
									}"
									data-testid="close"
									@click="toaster.dismiss(toast.id)">
									<span class="sr-only">Close</span>
									<XMarkIcon class="h-5 w-5" aria-hidden="true" />
								</button>
							</div>
						</div>
					</div>

					<Teleport v-if="toast.status == 'working'" to="body">
						<transition
							as="template"
							enter="ease-out duration-300"
							enter-from="opacity-0"
							enter-to="opacity-100"
							leave="ease-in duration-200"
							leave-from="opacity-100"
							leave-to="opacity-0">
							<div
								v-show="toast"
								class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity z-[55]"
								data-testid="overlay" />
						</transition>
					</Teleport>
				</div>
			</transition>
		</div>
	</div>
</template>

<script lang="ts" setup>
	import {
		CheckCircleIcon,
		XCircleIcon,
		ExclamationTriangleIcon,
		Cog6ToothIcon
	} from '@heroicons/vue/24/outline'
	import { XMarkIcon } from '@heroicons/vue/20/solid'
	import useToasterStore from '@/stores/toaster'
	const toaster = useToasterStore()

	const buttonClasses =
		'rounded-md bg-white text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2'

	function action(method: Function, id: number) {
		method()
		toaster.dismiss(id)
	}
</script>

<style>
	.working-icon {
		animation-duration: 2s !important;
	}
</style>
